import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';

import {
    Grid,
    TextField,
    Paper,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableCell,
    Button,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import Group from '@mui/icons-material/Group';
import ManageAccounts from '@mui/icons-material/ManageAccounts';

import { AddGroupDialog } from './components';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';

import ScheduleGroupsQuery from '../../../services/query/scheduleGroup/ScheduleGroupQuery.services';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { SG } from './scheduleGroups.classes';

const getResultIcons = (isActive: boolean) =>
    isActive ? (
        <Tooltip title="Success">
            <CheckCircleIcon className={SG.activeIconSuccess} sx={{ fontSize: '24px' }} color="success" />
        </Tooltip>
    ) : (
        <Tooltip title="Failed">
            <CancelIcon className={SG.activeIconError} sx={{ fontSize: '24px' }} color="error" />
        </Tooltip>
    );

export const ScheduleGroups = () => {
    const [groups, setGroups] = useState<any[]>();
    const [selectedGroup, setSelectedGroup] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [groupSearchText, setGroupSearchText] = useState<string>('');
    const [isGroupModalOpen, setIsGroupModalOpen] = useState<boolean>(false);

    const { t } = useTranslation();

    const history = useHistory();

    const onGroupModalClose = () => {
        setIsGroupModalOpen(false);
        setSelectedGroup(null);
    };

    const getGroups = useCallback(() => {
        setIsLoading(true);
        ScheduleGroupsQuery.GetScheduleGroups()
            .then((res: any) => {
                if (res) {
                    setIsLoading(false);
                    setGroups(res.data);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    }, [t]);

    const editGroup = (groupData: any) => {
        setSelectedGroup(groupData);
        setIsGroupModalOpen(true);
    };

    const editGroupUser = (groupId: any) => {
        history.push('/group-users/' + groupId);
    };

    const searchedGroups = useMemo(() => {
        if (!groupSearchText) return groups || [];

        return (groups || []).filter((group: any) =>
            group.groupName?.toLowerCase().includes(groupSearchText.toLowerCase())
        );
    }, [groupSearchText, groups]);

    useEffect(() => {
        getGroups();
    }, [getGroups]);

    return (
        <>
            <AddGroupDialog
                open={isGroupModalOpen}
                onClose={onGroupModalClose}
                getGroups={getGroups}
                selectedGroup={selectedGroup}
                setIsLoading={setIsLoading}
            />
            <SCUIContent className={SG.page}>
                <>
                    {isLoading && (
                        <div className={SG.loadingSection}>
                            <SCUILoadingLinear />
                        </div>
                    )}
                    <Grid className={SG.layout} container direction="row">
                        <Grid
                            className={SG.breadcrumbCol}
                            size={{
                                lg: 10,
                                md: 12,
                                xs: 12
                            }}>
                            <SCUIBreadCrumb title={t('scheduleGroup.header')} componentLink={'/schedule-groups'} />
                        </Grid>
                        <Grid
                            className={SG.addGroupCol}
                            size={{
                                lg: 2,
                                md: 6,
                                xs: 6
                            }}>
                            <SCUIButton
                                className={SG.addGroupButton}
                                text={t('scheduleGroup.addGroup')}
                                type="primary"
                                sizeType="fixed"
                                size={'m'}
                                onClick={() => setIsGroupModalOpen(true)}
                            />
                        </Grid>
                        {!isLoading && (searchedGroups?.length === 0 || !searchedGroups) ? (
                            <>
                                <Grid
                                    className={SG.emptyState}
                                    sx={{
                                        margin: '16px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    size={{
                                        lg: 12,
                                        md: 12,
                                        xs: 12
                                    }}>
                                    <Typography className={SG.emptyText} sx={{ fontSize: '24px', fontWeight: 'bold' }}>
                                        {t('scheduleGroup.noData')}
                                    </Typography>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid
                                    className={SG.searchRow}
                                    sx={{ marginBottom: 1, marginTop: 1 }}
                                    size={{
                                        lg: 12,
                                        md: 12,
                                        xs: 12
                                    }}>
                                    <TextField
                                        className={SG.searchInput}
                                        value={groupSearchText}
                                        onChange={(e: any) => setGroupSearchText(e.target.value)}
                                        fullWidth
                                        placeholder={t('scheduleGroup.groupName')}
                                    />
                                </Grid>
                                <Paper className={SG.tableOuterPaper} sx={{ padding: '2rem', margin: '2rem', width: '100%' }}>
                                    <TableContainer className={SG.tableContainer} component={Paper}>
                                        <Table className={SG.table} aria-label="operators">
                                            <TableHead className={SG.tableHead}>
                                                <TableRow className={SG.tableHeadRow}>
                                                    <TableCell className={SG.tableHeadCell} align="center">
                                                        {t('scheduleGroup.groupName')}
                                                    </TableCell>
                                                    <TableCell className={SG.tableHeadCell} align="center">
                                                        {t('scheduleGroup.groupEdit')}
                                                    </TableCell>
                                                    <TableCell className={SG.tableHeadCell} align="center">
                                                        {t('scheduleGroup.groupUserEdit')}
                                                    </TableCell>
                                                    <TableCell className={SG.tableHeadCell} align="center">
                                                        {t('scheduleGroup.groupActivation')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody className={SG.tableBody}>
                                                {(searchedGroups || []).map((row: any) => {
                                                    return (
                                                        <TableRow
                                                            className={SG.tableRow}
                                                            sx={{ '& > *': { borderBottom: 'unset' } }}
                                                            key={row.groupId}
                                                            data-group-id={row.groupId}
                                                        >
                                                            <TableCell
                                                                className={[SG.tableCell, SG.tableCellGroupName].join(' ')}
                                                                align="center"
                                                            >
                                                                {row.groupName}
                                                            </TableCell>
                                                            <TableCell className={[SG.tableCell, SG.tableCellEdit].join(' ')} align="center">
                                                                <Button className={SG.editGroupButton} onClick={() => editGroup(row)}>
                                                                    <Group className={SG.editGroupIcon} />
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell className={[SG.tableCell, SG.tableCellUsers].join(' ')} align="center">
                                                                <Button
                                                                    className={SG.editUsersButton}
                                                                    onClick={() => editGroupUser(row.groupId)}
                                                                >
                                                                    <ManageAccounts className={SG.editUsersIcon} />
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell className={[SG.tableCell, SG.tableCellActive].join(' ')} align="center">
                                                                <span className={SG.activeIconWrap}>
                                                                    {getResultIcons(row.isActive)}
                                                                </span>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </>
                        )}
                    </Grid>
                </>
            </SCUIContent>
        </>
    );
};
